import type {
  ModifierType,
  ModifierTypeToControlType,
} from "schemas/modifiers";

import * as React from "react";

import useShopStyles from "@v1/hooks/designLibrary/useGetDesignLibrarySavedStyles";
import {
  modifierTypeToSelector,
  selectTextModifierValuesWithSavedStylesReferences,
} from "@v1/selectors/modifiers";
import { useEditorSelector } from "@v1/store/store";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { getSavedStyleValue } from "replo-runtime/shared/savedStyles";
import { scrollToElement } from "replo-utils/dom/scroll";

export const useGetModifierControls = <T extends ModifierType>(
  modifierType: ModifierType,
  ref: React.RefObject<HTMLElement>,
): [
  Set<ModifierTypeToControlType[T]>,
  (
    newControl: ModifierTypeToControlType[T] | ModifierTypeToControlType[T][],
  ) => void,
] => {
  const logEvent = useLogAnalytics();
  const defaultControls = useGetModifierControlsAvailables<T>(modifierType);
  const [controls, setControls] =
    useOverridableState<Set<ModifierTypeToControlType[T]>>(defaultControls);

  const addControl = React.useCallback(
    (
      newControl: ModifierTypeToControlType[T] | ModifierTypeToControlType[T][],
    ) => {
      const newControls = new Set(controls);
      if (Array.isArray(newControl)) {
        for (const control of newControl) {
          newControls.add(control);
        }
      } else {
        newControls.add(newControl);
      }

      if (
        Array.isArray(newControl) &&
        newControl.some((control) => {
          return ["minWidth", "maxWidth", "minHeight", "maxHeight"].includes(
            control,
          );
        })
      ) {
        logEvent("design.modifier.add", {
          type: modifierType,
          propertyType: "minMaxWidthHeight",
        });
      } else if (typeof newControl === "string") {
        logEvent("design.modifier.add", {
          type: modifierType,
          propertyType: newControl,
        });
      }

      setControls(newControls);

      if (ref.current) {
        scrollToElement(ref.current);
      }
    },
    [controls, logEvent, modifierType, ref, setControls],
  );

  return [controls, addControl];
};

function useGetModifierControlsAvailables<T extends ModifierType>(
  modifierType: ModifierType,
): Set<ModifierTypeToControlType[T]> {
  const defaultControls: Set<ModifierTypeToControlType[T]> = useEditorSelector(
    modifierTypeToSelector[modifierType],
  );
  const textModifierValuesWithSavedStylesReferences = useEditorSelector(
    selectTextModifierValuesWithSavedStylesReferences,
  );
  const textModifierValuesWithSavedStylesReferencesControlTypes = Object.keys(
    textModifierValuesWithSavedStylesReferences,
  );

  const { shopStylesIncludingDeletedOnes: savedStyles } = useShopStyles();

  // NOTE (Sebas, 2024-11-13): In case we have a design library value, we only want to show
  // the alignment and color controls by default.
  if (
    modifierType === "text" &&
    Object.keys(textModifierValuesWithSavedStylesReferences).length > 0 &&
    // NOTE (Fran 2024-11-29): If the only saved style reference is the color we will handle this diferently.
    // We will handle this directly in the Dynamic Color Modifier.
    // This is because the color property is part of a group of other modifiers controls while
    // the text saved style is a group of properties.
    !(
      textModifierValuesWithSavedStylesReferencesControlTypes.length === 1 &&
      textModifierValuesWithSavedStylesReferencesControlTypes.includes("color")
    )
  ) {
    const firstTextModifierValueWithSavedStylesReferences = Object.values(
      textModifierValuesWithSavedStylesReferences,
    )[0]!;
    const savedStyleValue = getSavedStyleValue(
      savedStyles,
      String(firstTextModifierValueWithSavedStylesReferences),
    );

    if (!savedStyleValue?.deletedAt) {
      return new Set(["textAlign", "color"]) as Set<
        ModifierTypeToControlType[T]
      >;
    }
  }

  return defaultControls;
}
